import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Card, Form, Button, Alert, Spinner, Navbar, Image } from 'react-bootstrap';
import { useUserAuth } from '../context/UserAuthContext';

const UserLogin: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [mfaChallengeId, setMfaChallengeId] = useState<string | null>(null);
  const [otp, setOtp] = useState('');
  const [idleNotice, setIdleNotice] = useState<boolean>(false);
  const { login, verifyOtp, isLoading, error, isAuthenticated, clearError } = useUserAuth();
  const navigate = useNavigate();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/user/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // Show a notice if the previous session ended due to inactivity
  useEffect(() => {
    if (sessionStorage.getItem('idleLogout')) {
      setIdleNotice(true);
      sessionStorage.removeItem('idleLogout');
    }
  }, []);

  // Clear errors when component unmounts or inputs change
  useEffect(() => {
    return () => clearError();
  }, [clearError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    clearError();

    // Validation
    if (!email || !password) {
      setLocalError('Please enter both email and password');
      return;
    }

    if (!email.includes('@')) {
      setLocalError('Please enter a valid email address');
      return;
    }

    try {
      const result = await login(email, password);
      if (result?.mfaRequired && result.challengeId) {
        setMfaChallengeId(result.challengeId);
      }
      // Otherwise navigation happens automatically via useEffect when isAuthenticated changes
    } catch (err: any) {
      // Error is already set in context
      console.error('Login error:', err);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    clearError();
    if (!otp.trim() || !mfaChallengeId) {
      setLocalError('Please enter the code from your email');
      return;
    }
    try {
      await verifyOtp(mfaChallengeId, otp.trim());
      // Navigation happens automatically via useEffect when isAuthenticated changes
    } catch (err: any) {
      console.error('OTP error:', err);
    }
  };

  const resetToLogin = () => {
    setMfaChallengeId(null);
    setOtp('');
    setPassword('');
    setLocalError(null);
    clearError();
  };

  const displayError = localError || error;

  return (
    <div className="d-flex flex-column min-vh-100">
      {/* Header */}
      <Navbar bg="white" variant="light" className="mb-4 border-bottom shadow-sm">
        <Container>
          <Navbar.Brand>
            <Image 
              src="/qualrisk-logo.webp" 
              alt="QualRisk AI" 
              width="167" 
              height="30" 
              className="d-inline-block align-top me-2" 
            />
            QAI Secure File Transfer Manager
          </Navbar.Brand>
        </Container>
      </Navbar>

      {/* Main Content */}
      <Container className="flex-grow-1 d-flex align-items-center justify-content-center">
        <Card style={{ width: '100%', maxWidth: '450px' }} className="shadow">
          <Card.Body className="p-4">
            <div className="text-center mb-4">
              <h3 className="mb-2">{mfaChallengeId ? 'Enter your code' : 'Client Login'}</h3>
              <p className="text-muted">
                {mfaChallengeId
                  ? 'We emailed you a 6-digit login code. Enter it below.'
                  : 'Sign in with your email and password'}
              </p>
            </div>

            {idleNotice && !displayError && (
              <Alert variant="info" dismissible onClose={() => setIdleNotice(false)}>
                <i className="bi bi-clock-history me-2"></i>
                You were signed out after 15 minutes of inactivity. Please sign in again.
              </Alert>
            )}

            {displayError && (
              <Alert variant="danger" dismissible onClose={() => { setLocalError(null); clearError(); }}>
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                {displayError}
              </Alert>
            )}

            {mfaChallengeId ? (
              <Form onSubmit={handleVerifyOtp}>
                <Form.Group className="mb-3" controlId="otp">
                  <Form.Label>Login code</Form.Label>
                  <Form.Control
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="6-digit code"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    disabled={isLoading}
                    required
                    autoFocus
                  />
                </Form.Group>
                <Button variant="primary" type="submit" className="w-100 mb-3" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-shield-check me-2"></i>
                      Verify &amp; Sign In
                    </>
                  )}
                </Button>
                <div className="text-center">
                  <Button variant="link" size="sm" className="text-muted" onClick={resetToLogin} disabled={isLoading}>
                    Back to login
                  </Button>
                </div>
              </Form>
            ) : (
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3" controlId="email">
                <Form.Label>Email Address</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                  required
                  autoFocus
                />
              </Form.Group>

              <Form.Group className="mb-3" controlId="password">
                <Form.Label>Password</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                  required
                />
              </Form.Group>

              <Button 
                variant="primary" 
                type="submit" 
                className="w-100 mb-3"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Signing in...
                  </>
                ) : (
                  <>
                    <i className="bi bi-box-arrow-in-right me-2"></i>
                    Sign In
                  </>
                )}
              </Button>

              <div className="text-center">
                <small className="text-muted">
                  Access is by invitation only. Ask your administrator to invite you,
                  and you'll receive a temporary password by email.
                </small>
              </div>

              <div className="text-center mt-2">
                <small className="text-muted">
                  Trouble signing in?{' '}
                  <a href="mailto:support@qualrisk.com?subject=Secure%20File%20Transfer%20login%20help" className="text-decoration-none">
                    Contact support
                  </a>
                </small>
              </div>

              <hr className="my-4" />

              <div className="text-center">
                <div className="text-muted small mb-2">QualRisk staff</div>
                <Button variant="outline-secondary" size="sm" onClick={() => navigate('/admin/login')}>
                  <i className="bi bi-microsoft me-2"></i>
                  Admin sign in
                </Button>
              </div>
            </Form>
            )}
          </Card.Body>
        </Card>
      </Container>

      {/* Footer */}
      <footer className="bg-light py-3 mt-4">
        <Container>
          <div className="text-center text-muted">
            &copy; {new Date().getFullYear()} QAI Secure File Transfer Manager
          </div>
        </Container>
      </footer>
    </div>
  );
};

export default UserLogin;

